// src/components/data_visualizations/PreviousSolutions.js
import React, { useContext, useState, useMemo } from 'react';
import {
  Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem,
  Chip, Stack, Card, CardContent, Tooltip, IconButton
} from '@mui/material';
import {
  PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer
} from 'recharts';
import { DataGrid } from '@mui/x-data-grid';
import { DataAndFilterContext } from '../../context/DataAndFilterContext';
import CustomerModal from '../CustomerModal';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SolutionTile from '../tiles/SolutionTile';
import TriggerTile from '../tiles/TriggerTile';


// Define colors for the charts
const PIE_COLORS = ['#FF7F50', '#87CEEB', '#9370DB', '#FFD700', '#FF6347', '#00C9A7', '#9B59B6', '#2ECC71', '#E67E22'];

// ----------------- tiny helpers -----------------
const take = (arr, n) => (Array.isArray(arr) ? arr.slice(0, n) : []);
const randSample = (arr, n) => {
  const a = Array.isArray(arr) ? [...arr] : [];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, n);
};
const asNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
const cap = (s) => {
  const t = (s || '').toString().trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
};

// Pull up to 2 receipts for a reason node (reason_not_good_enough_* fields) or generic {quote,timestamp}
function extractReceiptsFromReason(reason, fallbackStory) {
  const out = [];
  const push = (q, ts) => {
    const qq = (q || '').toString().trim();
    if (!qq) return;
    out.push({ quote: qq, timestamp: Array.isArray(ts) ? ts[0] : ts || '' });
  };

  // Dedicated fields
  if (reason?.reason_not_good_enough_quote) {
    push(
      reason.reason_not_good_enough_quote,
      reason.reason_not_good_enough_timestamps || reason.reason_not_good_enough_timestamp
    );
  }
  if (reason?.quote) push(reason.quote, reason.timestamp);

  // If nothing, try story-level (very light fallback)
  if (!out.length && fallbackStory?.representative_quotes) {
    for (const q of fallbackStory.representative_quotes) {
      if (q?.quote) push(q.quote, q.timestamp);
      if (out.length >= 2) break;
    }
  }
  return take(out, 2);
}


// -------- dashboard-style builders (match Product view tiles) --------
const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

// Reuses your small extract for quotes, but broader (handles *_quote + *_timestamp[s])
function extractReceiptsGeneric(node, limit = 2) {
  if (!node) return [];
  const out = [];
  const push = (q, ts) => {
    const qq = (q || '').toString().trim();
    if (!qq) return;
    out.push({ quote: qq, url: '', timestamp: Array.isArray(ts) ? ts[0] : ts || '' });
  };

  // generic arrays
  const arrs = ['receipts', 'quotes', 'representative_quotes', 'evidence'];
  for (const k of arrs) {
    const v = node?.[k];
    const list = Array.isArray(v) ? v : (v && typeof v === 'object' ? Object.values(v) : []);
    for (const r of list) {
      if (typeof r === 'string') push(r, r.timestamp || r.ts);
      else if (r && typeof r === 'object') push(r.quote || r.text || r.value || r.snippet, r.timestamp || r.ts);
      if (out.length >= limit) return out;
    }
  }

  // schema-specific *_quote + *_timestamp
  if (node && typeof node === 'object') {
    for (const k of Object.keys(node)) {
      if (/_quote$/.test(k) && node[k]) {
        const base = k.replace(/_quote$/, '');
        const ts = node[`${base}_timestamp`] ?? node[`${base}_timestamps`];
        push(node[k], ts);
        if (out.length >= limit) return out;
      }
    }
    if (node.quote) push(node.quote, node.timestamp);
  }
  return out.slice(0, limit);
}

/** Build "Previous Solution" tiles (exactly like Dashboard) */
function buildPreviousSolutionTiles(stories) {
  const byPrev = new Map();

  for (const s of stories || []) {
    const title = s.previous_solution_category || s.previous_solution || '';
    if (!title) continue;
    const key = String(title).toLowerCase();
    const bucket = byPrev.get(key) || { title, count: 0, reasons: [] };
    bucket.count += 1;

    const reasons = Array.isArray(s.reasons_not_good_enough) ? s.reasons_not_good_enough : [];
    for (const r of reasons) {
      const reason =
        r.reason_not_good_enough_category ||
        r.reason_not_good_enough ||
        r.reason ||
        r.title ||
        '';
      if (!reason) continue;

      const receipts =
        extractReceiptsFromReason(r, s).length
          ? extractReceiptsFromReason(r, s)
          : extractReceiptsGeneric(r, 2).length
          ? extractReceiptsGeneric(r, 2)
          : extractReceiptsGeneric(s, 2);

      const existing = bucket.reasons.find(
        (rr) => (rr.reason || '').toLowerCase() === reason.toLowerCase()
      );
      if (existing) {
        existing.count = (existing.count || 0) + 1;
        existing.receipts = (existing.receipts || []).concat(receipts).slice(0, 2);
      } else {
        bucket.reasons.push({ reason, count: 1, receipts });
      }
    }

    byPrev.set(key, bucket);
  }

  const tiles = [];
  for (const b of byPrev.values()) {
    b.reasons.forEach((r) => {
      r.percent = b.count ? Math.round((r.count / b.count) * 100) : undefined;
    });
    b.reasons.sort((a, b) => b.count - a.count);
    tiles.push({
      type: 'solution',
      subtype: 'previous',
      title: b.title,
      count: b.count,
      reasons: b.reasons.slice(0, 2),
    });
  }

  return tiles.sort((a, b) => b.count - a.count).slice(0, 8); // show up to 8 like your dashboard caps
}

/** Build "Trigger" tiles (exactly like Dashboard) */
function buildTriggerTiles(stories) {
  const buckets = new Map();

  for (const s of stories || []) {
    // single-field form
    const solo = s.trigger_category || s.trigger;
    if (solo) {
      const title = s.trigger_category || solo;
      const k = String(title).toLowerCase();
      const b = buckets.get(k) || { title, count: 0, receipts: [] };
      b.count += 1;
      // dashboard receipts: the trigger text itself
      const rec = { quote: (s.trigger || title).toString(), url: '' };
      if (!b.receipts.find((r) => r.quote === rec.quote)) b.receipts.push(rec);
      buckets.set(k, b);
    }

    // array forms
    const arrays = [s.triggers, s.switching_triggers, s.why_customers_switch].filter(Boolean);
    for (const arr of arrays) {
      for (const t of arr) {
        const title = t?.trigger_category || t?.title || t?.reason || t?.text || '';
        if (!title) continue;
        const k = String(title).toLowerCase();
        const b = buckets.get(k) || { title, count: 0, receipts: [] };
        b.count += 1;
        const rec = { quote: (t?.trigger || t?.text || title).toString(), url: '' };
        if (rec.quote && !b.receipts.find((r) => r.quote === rec.quote)) b.receipts.push(rec);
        buckets.set(k, b);
      }
    }
  }

  return Array.from(buckets.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 8)
    .map((t) => ({ type: 'trigger', title: t.title, count: t.count, receipts: t.receipts.slice(0, 2) }));
}



// ----------------- main component -----------------
function PreviousSolutions() {
  const { filteredData } = useContext(DataAndFilterContext);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [page, setPage] = useState(1);
  const [sortModel, setSortModel] = useState([{ field: 'conversation_date', sort: 'desc' }]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const ITEMS_PER_PAGE = 10;

  // section totals for percent pills
  const sectionTotal = filteredData.length;

  // Dashboard-style tiles (top lists)
  const prevSolutionTiles = useMemo(
    () => buildPreviousSolutionTiles(filteredData),
    [filteredData]
  );
  const triggerTiles = useMemo(
    () => buildTriggerTiles(filteredData),
    [filteredData]
  );
  

  // Only items that actually mention a previous solution
  const filteredAndValidData = useMemo(() => {
    return filteredData.filter((item) => item.previous_solution || item.previous_solution_category);
  }, [filteredData]);

  // ----------- Pie: Previous Solutions -----------
  const previousSolutionData = useMemo(() => {
    const categoryCount = {};
    filteredAndValidData.forEach((item) => {
      const category = item.previous_solution_category;
      if (category) {
        categoryCount[category] = (categoryCount[category] || 0) + 1;
      }
    });

    const totalFiles = filteredAndValidData.length || 1;
    return Object.keys(categoryCount)
      .map((category) => ({
        name: category,
        value: categoryCount[category],
        percentage: ((categoryCount[category] / totalFiles) * 100).toFixed(2),
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredAndValidData]);

  // ----------- Pie: Switching Triggers (by trigger_category) -----------
  const triggerPieData = useMemo(() => {
    // Gather trigger_category from:
    // - simple top-level: trigger_category
    // - arrays: triggers / switching_triggers / why_customers_switch (objects with trigger_category)
    const counts = new Map();

    const bump = (title) => {
      if (!title) return;
      const k = String(title).trim();
      counts.set(k, (counts.get(k) || 0) + 1);
    };

    for (const s of filteredData) {
      if (s.trigger_category) bump(s.trigger_category);

      const arrays = [s.triggers, s.switching_triggers, s.why_customers_switch].filter(Boolean);
      for (const arr of arrays) {
        for (const t of arr) {
          bump(t?.trigger_category || t?.title || t?.reason || t?.text);
        }
      }
    }

    const total = Array.from(counts.values()).reduce((a, c) => a + c, 0) || 1;
    return Array.from(counts.entries())
      .map(([name, value]) => ({
        name,
        value,
        percentage: ((value / total) * 100).toFixed(2),
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData]);

  // ----------- DataGrid data -----------
  const filteredTableData = useMemo(() => {
    const rows = filteredAndValidData
      .filter((item) => (selectedCategory ? item.previous_solution_category === selectedCategory : true))
      .sort((a, b) => {
        if (sortModel[0].field === 'conversation_date') {
          return sortModel[0].sort === 'asc'
            ? new Date(a.conversation_date) - new Date(b.conversation_date)
            : new Date(b.conversation_date) - new Date(a.conversation_date);
        }
        return 0;
      });
    return rows.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  }, [filteredAndValidData, selectedCategory, sortModel, page]);

  const totalTablePages = Math.ceil(
    filteredAndValidData.filter((item) => (selectedCategory ? item.previous_solution_category === selectedCategory : true)).length / ITEMS_PER_PAGE
  );

  // ----------- Comparison Grid -----------
  const comparison = useMemo(() => {
    // Rows = chosen solution (solution_choice_category)
    // Cols = previous_solution_category
    const rowKeys = new Set();
    const colKeys = new Set();

    for (const s of filteredAndValidData) {
      if (s.solution_choice_category) rowKeys.add(s.solution_choice_category);
      if (s.previous_solution_category) colKeys.add(s.previous_solution_category);
    }

    const rows = Array.from(rowKeys).sort();
    const cols = Array.from(colKeys).sort();

    // Pre-index data per (row, col)
    const cellMap = new Map(); // `${row}|||${col}` -> { stories: [] }
    const rowTotals = new Map(); // row -> total stories with that chosen solution

    for (const row of rows) rowTotals.set(row, 0);
    for (const s of filteredAndValidData) {
      const row = s.solution_choice_category || '';
      if (!row) continue;
      rowTotals.set(row, (rowTotals.get(row) || 0) + 1);

      const col = s.previous_solution_category || '';
      if (!col) continue;

      const key = `${row}|||${col}`;
      const cur = cellMap.get(key) || { stories: [] };
      cur.stories.push(s);
      cellMap.set(key, cur);
    }

    // Aggregate each cell
    const buildCell = (stories, rowTotal) => {
      const count = stories.length;
      const percent = rowTotal ? Math.round((count / rowTotal) * 100) : 0;

      if (count === 0) {
        return { count, percent, topReasons: [], topTriggers: [] };
      }

      // Top 2 "reasons_not_good_enough" categories (with receipts)
      const reasonBuckets = new Map();
      for (const s of stories) {
        for (const r of (s.reasons_not_good_enough || [])) {
          const name = r.reason_not_good_enough_category || r.reason || r.title || '';
          if (!name) continue;
          const b = reasonBuckets.get(name) || { title: name, count: 0, receipts: [] };
          b.count += 1;
          b.receipts = b.receipts.concat(extractReceiptsFromReason(r, s)).slice(0, 4); // keep a small pool
          reasonBuckets.set(name, b);
        }
      }
      const topReasons = Array.from(reasonBuckets.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 2)
        .map((r) => ({
          title: r.title,
          count: r.count,
          percent: count ? Math.round((r.count / count) * 100) : 0,
          receipts: randSample(r.receipts, 2),
        }));

      // Top trigger_category values (count + percent)
      const triggerCounts = new Map();
      for (const s of stories) {
        if (s.trigger_category) {
          triggerCounts.set(s.trigger_category, (triggerCounts.get(s.trigger_category) || 0) + 1);
        }
        const arrays = [s.triggers, s.switching_triggers, s.why_customers_switch].filter(Boolean);
        for (const arr of arrays) {
          for (const t of arr) {
            const title = t?.trigger_category || t?.title || t?.reason || t?.text;
            if (!title) continue;
            triggerCounts.set(title, (triggerCounts.get(title) || 0) + 1);
          }
        }
      }
      const topTriggers = Array.from(triggerCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([title, c]) => ({
          title,
          count: c,
          percent: count ? Math.round((c / count) * 100) : 0,
        }));

      return { count, percent, topReasons, topTriggers };
    };

    const table = rows.map((row) => {
      const rowTotal = rowTotals.get(row) || 0;
      const cells = cols.map((col) => {
        const key = `${row}|||${col}`;
        const stories = cellMap.get(key)?.stories || [];
        return buildCell(stories, rowTotal);
      });
      return { row, rowTotal, cells };
    });

    return { rows, cols, table };
  }, [filteredAndValidData]);

  // ------------- handlers & modal -------------
  const handleRowClick = (customer) => {
    setSelectedCustomer(customer);
    setModalOpen(true);
  };
  const handleModalClose = () => setModalOpen(false);

  // ------------- render -------------
  return (
    <Box>
      {/* Charts row */}
      <Grid container spacing={4}>
        {/* Pie Chart: Previous Solutions */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6">Previous Solutions</Typography>
          <Typography variant="body2" gutterBottom>
            These are the previous solutions customers mentioned using.
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={previousSolutionData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name }) => name}
              >
                {previousSolutionData.map((entry, index) => (
                  <Cell key={`cell-prev-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <RTooltip
                formatter={(value, name, entry) => [`${value} (${entry.payload.percentage}%)`, 'Files']}
              />
            </PieChart>
          </ResponsiveContainer>
        </Grid>

        {/* Pie Chart: Switching Triggers (duplicate style, different data) */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6">Switching Triggers</Typography>
          <Typography variant="body2" gutterBottom>
            The most common triggers customers cited before switching.
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={triggerPieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name }) => name}
              >
                {triggerPieData.map((entry, index) => (
                  <Cell key={`cell-trig-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <RTooltip
                formatter={(value, name, entry) => [`${value} (${entry.payload.percentage}%)`, 'Occurrences']}
              />
            </PieChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>


      {/* NEW: Dashboard-style tiles */}
      <Box mt={4}>
        <Typography variant="h6" sx={{ mb: 1 }}>Top Previous Solutions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Matches the “Previous Solution” tiles on the dashboard.
        </Typography>
        <Grid container spacing={2}>
          {prevSolutionTiles.map((t, i) => (
            <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
              <SolutionTile {...t} sectionTotal={sectionTotal} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box mt={4}>
        <Typography variant="h6" sx={{ mb: 1 }}>Top Triggers</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Matches the “Trigger” tiles on the dashboard.
        </Typography>
        <Grid container spacing={2}>
          {triggerTiles.map((t, i) => (
            <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
              <TriggerTile {...t} sectionTotal={sectionTotal} />
            </Grid>
          ))}
        </Grid>
      </Box>


      {/* (Optional) Table Section retained for exploration/filtering */}
      <Box mt={4}>
        <Typography variant="h6">Previous Solutions Data</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Previous Solution Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {previousSolutionData.map((item) => (
              <MenuItem key={item.name} value={item.name}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box height={400}>
          <DataGrid
            rows={filteredTableData.map((item, index) => ({
              id: index,
              ...item,
              conversation_date: new Date(item.conversation_date).toLocaleDateString(),
              reasons_not_good_enough: Array.isArray(item.reasons_not_good_enough)
                ? item.reasons_not_good_enough
                    .map(r => r.reason_not_good_enough_category || r.title || r.reason || '')
                    .filter(Boolean)
                    .join(', ')
                : ''
            }))}
            columns={[
              {
                field: 'customer_name', headerName: 'Name', flex: 1,
                renderCell: (params) => (
                  <Typography sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(params.row)}>
                    {params.row.customer_name}
                  </Typography>
                )
              },
              { field: 'conversation_date', headerName: 'Conversation Date', flex: 1, sortable: true },
              { field: 'previous_solution', headerName: 'Previous Solution', flex: 1 },
              { field: 'previous_solution_category', headerName: 'Prev. Category', flex: 1 },
              { field: 'solution_choice_category', headerName: 'Chosen Solution', flex: 1 },
              { field: 'stickiness_level_of_previous_solution', headerName: 'Stickiness Level', flex: 1, sortable: true },
            ]}
            pageSize={ITEMS_PER_PAGE}
            rowsPerPageOptions={[5, 10, 20]}
            sortingOrder={['asc', 'desc']}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
            pagination
            paginationMode="client"
            rowCount={totalTablePages * ITEMS_PER_PAGE}
            page={page - 1}
            onPageChange={(params) => setPage(params + 1)}
          />
        </Box>
      </Box>

      {/* Customer Modal */}
      <CustomerModal open={modalOpen} onClose={handleModalClose} customer={selectedCustomer} />
    </Box>
  );
}

export default PreviousSolutions;
